import { useEffect, useRef } from 'react';

interface ScrollAnimationOptions {
    items: any[];
    globalStartTime: number;
    itemHeight: number;
    pauseDuration?: number;
    scrollDuration?: number;
    animationPrefix?: string;
    enabled?: boolean;
}

export const useScrollAnimation = ({
    items,
    globalStartTime,
    itemHeight,
    pauseDuration = 5,
    scrollDuration = 0.5,
    animationPrefix = 'scroll',
    enabled = true
}: ScrollAnimationOptions) => {
    const listRef = useRef<HTMLUListElement>(null);
    const animationIdRef = useRef<number>();

    useEffect(() => {
        const list = listRef.current;

        if (!enabled || !list || items.length === 0 || items.length === 1) {
            if (list) list.style.transform = 'translateY(0px)';
            return;
        }

        const originalItemCount = items.length / 2;

        const TICK_MS = 5_000;
        const SCROLL_MS = scrollDuration * 1_000;
        const scrollStart = TICK_MS - SCROLL_MS;

        const syncAnimation = () => {
            const now = Date.now();
            const msInTick = now % TICK_MS;
            const globalTick = Math.floor(now / TICK_MS);
            const currentIndex = globalTick % originalItemCount;
            const nextIndex = (currentIndex + 1) % originalItemCount;

            let translateY: number;

            if (msInTick < scrollStart) {
                // Pause phase — hold current item
                translateY = currentIndex * itemHeight;
            } else {
                // Scroll phase — animate toward next item
                const scrollProgress = (msInTick - scrollStart) / SCROLL_MS;

                // Ease out cubic — decelerates into position
                const eased = scrollProgress < 0.5
                    ? 4 * scrollProgress * scrollProgress * scrollProgress
                    : 1 - Math.pow(-2 * scrollProgress + 2, 3) / 2;

                const from = currentIndex * itemHeight;
                const to = nextIndex === 0
                    ? originalItemCount * itemHeight
                    : nextIndex * itemHeight;

                translateY = from + eased * (to - from);
            }

            const maxPosition = originalItemCount * itemHeight;
            translateY = Math.max(0, translateY % maxPosition);

            list.style.transform = `translateY(-${translateY}px)`;
            animationIdRef.current = requestAnimationFrame(syncAnimation);
        };

        animationIdRef.current = requestAnimationFrame(syncAnimation);

        return () => {
            if (animationIdRef.current) cancelAnimationFrame(animationIdRef.current);
        };
    }, [items, itemHeight, scrollDuration, enabled]);

    return { listRef };
};